import { defineRecipe } from '@plurall/elo-v3';

export const PerformanceDashboardHeaderRootRecipe = defineRecipe({
  className: 'perf-header',
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'space.32',
    marginBlockEnd: 'space.24',
    paddingInline: 'padding.large',
    sm: {
      paddingInline: 'padding.2xlarge',
    },
  },
});

export const PerformanceDashboardHeaderRowRecipe = defineRecipe({
  className: 'perf-header__row',
  base: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 'space.8',
  },
});

export const PerformanceDashboardHeaderTitleGroupRecipe = defineRecipe({
  className: 'perf-header__title-group',
  base: {
    display: 'flex',
    flex: '1 1 0%',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    height: 'auto',
    gap: 'space.8',
    minWidth: 0,
    overflow: 'hidden',

    '& > button': {
      flexShrink: 0,
    },

    '& > h1': {
      flex: '1 1 auto',
      minWidth: 0,
      marginBlockStart: 'space.16',
    },
  },
});

export const PerformanceDashboardHeaderTitleRecipe = defineRecipe({
  className: 'perf-header__title',
  base: {
    margin: 0,
    fontFamily: 'body',
    fontWeight: 'font.weight.bold',
    fontStyle: 'normal',
    fontSize: 'font.size.3xlarge',
    lineHeight: '138%',
    letterSpacing: '0',
    verticalAlign: 'middle',
    color: 'text.primary',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

export const PerformanceDashboardHeaderContentRecipe = defineRecipe({
  className: 'perf-header__content',
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'space.8',
    minWidth: 0,
  },
});

export const PerformanceDashboardHeaderSubtitleRecipe = defineRecipe({
  className: 'perf-header__subtitle',
  base: {
    margin: 0,
    fontFamily: 'body',
    fontStyle: 'normal',
    fontSize: 'font.size.medium',
    lineHeight: '150%',
    letterSpacing: '0',
    color: 'text.secondary',
  },
});

export const PerformanceDashboardHeaderActionsRecipe = defineRecipe({
  className: 'perf-header__actions',
  base: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 'space.16',
  },
});

export const PerformanceDashboardHeaderFilterGroupRecipe = defineRecipe({
  className: 'perf-header__filter-group',
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: 'space.8',
    minWidth: '220px',
  },
});

export const PerformanceDashboardHeaderFilterLabelRecipe = defineRecipe({
  className: 'perf-header__filter-label',
  base: {
    margin: 0,
    color: 'text.secondary',
    fontSize: 'font.size.small',
    fontWeight: 'font.weight.semi.bold',
    lineHeight: 'short',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
});

export const PerformanceDashboardYearGroupRecipe = defineRecipe({
  className: 'perf-header__year-group',
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: 'space.8',
    zIndex: 9,
    minWidth: '120px',
    maxWidth: '100%',
    justifyContent: 'flex-end',
    '& > div:first-of-type': {
      display: 'flex',
      alignItems: 'center',
      gap: 'space.8',
      minWidth: '175px',
    },
    '& > div:first-of-type > span': {
      margin: 0,
      color: 'text.secondary',
      fontSize: 'font-size-small',
      fontWeight: 'font.weight.semi.bold',
      lineHeight: 'short',
      whiteSpace: 'nowrap',
      flexShrink: 0,
    },
    "& [data-scope='field'][data-part='root']": {
      width: '100% !important',
    },
    "& [data-scope='select'][data-part='root'], & [data-scope='select'][data-part='trigger']": {
      width: '100% !important',
    },
    "& [data-scope='select'][data-part='value-text']": {
      maxWidth: 'none !important',
    },
    "& [data-scope='select'][data-part='content'], & [data-scope='select'][data-part='positioner']": {
      minWidth: '100%',
    },
    '@media (max-width: 768px)': {
      width: '100%',
      justifyContent: 'space-between',
      alignItems: 'center',
      '& > div:first-of-type': {
        flex: 1,
        minWidth: 0,
      },
    },
    // Seletor de ano e menu de ações (⋮) são controles, não conteúdo do
    // relatório. Declarado por último para vencer o bloco de max-width acima,
    // já que na impressão a largura do papel (~698px) cai nele.
    _print: { display: 'none' },
  },
});

export const PerformanceDashboardActionsTriggerRecipe = defineRecipe({
  className: 'perf-header__actions-trigger',
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 'size.40',
    height: 'size.40',
    padding: 0,
    border: 'none',
    borderRadius: 'shape.8',
    background: 'transparent',
    color: 'foreground.primary',
    cursor: 'pointer',
    transition: 'background-color 150ms ease-in-out, color 150ms ease-in-out',
    _hover: {
      background: 'alpha.gray.16',
    },
    '&[data-state="open"]': {
      background: 'alpha.gray.16',
    },
    _focusVisible: {
      outline: '2px solid',
      outlineColor: 'action.focus.ring.primary',
      outlineOffset: '2px',
    },
  },
});

export const PerformanceDashboardActionsContentRecipe = defineRecipe({
  className: 'perf-header__actions-content',
  base: {
    minWidth: '168px',
    padding: 'space.8',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'border.secondary',
    borderRadius: 'shape.16',
    background: 'background.primary',
    boxShadow: 'var(--shadow-md, 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1))',
    overflow: 'hidden',
  },
});

export const PerformanceDashboardActionsItemRecipe = defineRecipe({
  className: 'perf-header__actions-item',
  base: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    height: 'size.40',
    paddingInline: 'space.8',
    border: 'none',
    borderRadius: 'shape.8',
    background: 'transparent',
    color: 'text.primary',
    cursor: 'pointer',
    fontSize: 'font-size-medium',
    fontWeight: 'font-weight-regular',
    lineHeight: 'short',
    textAlign: 'start',
    whiteSpace: 'nowrap',
    transition: 'background-color 150ms ease-in-out',
    _hover: {
      background: 'alpha.gray.16',
    },
    '&[data-highlighted]': {
      background: 'alpha.gray.16',
    },
    _focusVisible: {
      outline: '2px solid',
      outlineColor: 'action.focus.ring.primary',
      outlineOffset: '2px',
    },
  },
});

export const PerformanceDashboardMobileActionsListRecipe = defineRecipe({
  className: 'perf-header__mobile-actions-list',
  base: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 0,
    width: '100%',
  },
});

export const PerformanceDashboardMobileActionsItemRecipe = defineRecipe({
  className: 'perf-header__mobile-actions-item',
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: 'space.8',
    width: '100%',
    minHeight: 'size.40',
    paddingInline: 'space.8',
    border: 'none',
    borderRadius: 0,
    background: 'transparent',
    color: 'text.primary',
    cursor: 'pointer',
    fontSize: 'font-size-medium',
    fontWeight: 'font-weight-regular',
    lineHeight: 'short',
    textAlign: 'start',
    transition: 'background-color 150ms ease-in-out',
    _active: {
      background: 'alpha.gray.32',
    },
    _focus: {
      background: 'alpha.gray.24',
    },
    _focusVisible: {
      outline: '2px solid',
      outlineColor: 'action.focus.ring.primary',
      outlineOffset: '2px',
    },
  },
});

export const PerformanceDashboardFiltersFormRecipe = defineRecipe({
  className: 'perf-header__filters',
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'space.8',
    // Controles de busca não fazem parte do relatório impresso.
    _print: { display: 'none' },
    "& [data-scope='field'][data-part='root']": {
      width: '100% !important',
    },
    "& [data-scope='select'][data-part='root'], & [data-scope='select'][data-part='trigger']": {
      width: '100% !important',
    },
    "& [data-scope='select'][data-part='value-text']": {
      maxWidth: 'none !important',
    },
    "& [data-scope='select'][data-part='content'], & [data-scope='select'][data-part='positioner']": {
      minWidth: '100%',
    },
    "& [data-scope='combobox'][data-part='root'], & [data-scope='combobox'][data-part='control'], & [data-scope='combobox'][data-part='input']":
      {
        width: '100% !important',
      },
    "& [data-scope='combobox'][data-part='content'], & [data-scope='combobox'][data-part='positioner']": {
      minWidth: '100%',
    },
    "& [data-scope='combobox'][data-part='control']": {
      height: '43px !important',
    },
  },
});

export const PerformanceDashboardFiltersRowRecipe = defineRecipe({
  className: 'perf-header__filters-row',
  base: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'stretch',
    gap: 'space.8',
    width: '100%',
    '& > *': {
      flex: 1,
      minWidth: '140px',
      display: 'flex',
    },
    '@media (max-width: 768px)': {
      '& > *': {
        flex: '0 0 100% !important',
        flexBasis: '100% !important',
        width: '100% !important',
        maxWidth: '100%',
        minWidth: 0,
      },
    },
  },
});

export const PerformanceDashboardFilterSelectRecipe = defineRecipe({
  className: 'perf-header__filter-select',
  base: {
    flex: 1,
    minWidth: 0,
    width: '100%',
  },
});

export const PerformanceDashboardSearchButtonWrapperRecipe = defineRecipe({
  className: 'perf-header__search-btn-wrapper',
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    '@media (max-width: 768px)': {
      '& button': {
        width: '100%',
      },
    },
  },
});

export const PerformanceDashboardSearchDropdownRecipe = defineRecipe({
  className: 'perf-search-dropdown',
  base: {
    "& [data-scope='combobox'][data-part='item']": {
      overflow: 'hidden',
      maxWidth: '100%',
      minWidth: 0,
      '& > *': {
        minWidth: 0,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
      },
    },
  },
});

export const PerformanceDashboardResultsRecipe = defineRecipe({
  className: 'perf-header__results',
  base: {
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 'space.8',
    marginBlockStart: 0,
    paddingBlockStart: 0,
  },
});

export const PerformanceDashboardResultsSkeletonRecipe = defineRecipe({
  className: 'perf-header__results-skeleton',
  base: {
    marginBlockStart: 0,
    paddingBlockStart: 0,
  },
});

export const PerformanceDashboardResultsSkeletonListRecipe = defineRecipe({
  className: 'perf-header__results-skeleton-wrapper',
  base: {
    display: 'flex',
    justifyContent: 'space-between',
  },
});

export const PerformanceDashboardResultsSkeletonItemRecipe = defineRecipe({
  className: 'perf-header__results-skeleton-item',
  base: {},
});

export const PerformanceDashboardResultsSkeletonSmallWrapperRecipe = defineRecipe({
  className: 'perf-header__results-skeleton-wrapper',
  base: { width: '200px' },
});

export const PerformanceDashboardResultsSkeletonMediumWrapperRecipe = defineRecipe({
  className: 'perf-header__results-skeleton-wrapper',
  base: {
    width: '320px',
    display: 'flex',
    justifyContent: 'space-between',
  },
});

export const PerformanceDashboardResultsTitleRecipe = defineRecipe({
  className: 'perf-header__results-title',
  base: {
    margin: 0,
    fontFamily: 'body',
    fontWeight: 'font.weight.semibold',
    fontStyle: 'normal',
    fontSize: 'font.size.large',
    lineHeight: '138%',
    letterSpacing: '0',
    verticalAlign: 'middle',
    color: 'text.primary',
  },
});

export const PerformanceDashboardResultsMetricsRecipe = defineRecipe({
  className: 'perf-header__results-metrics',
  base: {
    display: 'flex',
    alignItems: 'center',
  },
});

export const PerformanceDashboardMetricRecipe = defineRecipe({
  className: 'perf-header__metric',
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'space.4',
    fontFamily: 'body',
    fontSize: 'font.size.small',
    lineHeight: '150%',
    letterSpacing: '0',
    color: 'text.tertiary',
    '& span': {
      display: 'inline-flex',
      alignItems: 'center',
      fontFamily: 'inherit',
      fontWeight: 'font.weight.regular',
      fontStyle: 'normal',
      fontSize: 'inherit',
      lineHeight: 'inherit',
      letterSpacing: 'inherit',
    },
    '& strong': {
      fontFamily: 'inherit',
      fontWeight: 'font.weight.bold',
      fontStyle: 'normal',
      fontSize: 'inherit',
      lineHeight: 'inherit',
      letterSpacing: 'inherit',
      marginInlineStart: 'space.8',
      transform: 'translateY(0.5px)',
      color: 'text.primary',
    },
  },
});

export const PerformanceDashboardMetricWithSeparatorRecipe = defineRecipe({
  className: 'perf-header__metric--with-sep',
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'space.4',
    fontFamily: 'body',
    fontSize: 'font.size.small',
    lineHeight: '150%',
    letterSpacing: '0',
    color: 'text.tertiary',
    marginInlineStart: 'space.16',
    borderLeftWidth: '2px',
    borderLeftStyle: 'solid',
    borderLeftColor: 'border.primary',
    paddingInlineStart: 'space.16',
    '& span': {
      display: 'inline-flex',
      alignItems: 'center',
      fontFamily: 'inherit',
      fontWeight: 'font.weight.regular',
      fontStyle: 'normal',
      fontSize: 'inherit',
      lineHeight: 'inherit',
      letterSpacing: 'inherit',
    },
    '& strong': {
      fontFamily: 'inherit',
      fontWeight: 'font.weight.bold',
      fontStyle: 'normal',
      fontSize: 'inherit',
      lineHeight: 'inherit',
      letterSpacing: 'inherit',
      marginInlineStart: 'space.8',
      transform: 'translateY(0.5px)',
      color: 'text.primary',
    },
  },
});
